import React, { useContext } from "react";

import { useTranslation } from "react-i18next";

import {
	LookupContext,
	OfferOptionsContext,
	OfferStoreContext,
	SportOfferContext,
} from "@gp/components";
import { SportOffer } from "@gp/offer";
import { runInAction } from "mobx";
import { observer } from "mobx-react";

import { Dropdown } from "./column-type-selectors";

export const ColumnTypeSelectorsPrematch = observer(
	function ColumnTypeSelectorsPrematch() {
		const sport = useContext(SportOfferContext) as SportOffer;
		const viewStore = useContext(OfferStoreContext);
		const lookups = useContext(LookupContext);
		const options = useContext(OfferOptionsContext);
		const t = useTranslation().t;

		const sportSelectors =
			viewStore.configuration.bettingTypeSelectorsStore.getSportSelector(
				sport
			) || {};

		const availableSelectors = sport.headers?.selectors.filter((s) =>
			s.primary.some((p) => lookups.bettingTypesByAbrv.has(p))
		);

		const allAvailableMapped = availableSelectors?.map((s) => ({
			abrv: s.name,
			displayName:
				lookups.bettingTypesByAbrv.get(s.name)?.simpleName ||
				t("BETTING_TYPE.HEADER." + s.name),
		}));

		function updateSelectors(column: string, newSelector: string) {
			runInAction(() => {
				// NOTE: every change on sportSelectors will update state that is why it is wrapped in the runInAction

				// pick selector configuration for available sport selectors
				const chosenSelector = availableSelectors?.find(
					(s) => s.name === newSelector
				);

				if (chosenSelector == null) {
					console.error("Failed to find chosen selector");
					return;
				}

				const existingSelectorColumn =
					Object.values(sportSelectors).findIndex(
						(ss) => ss?.name === chosenSelector?.name
					) + 1; // +1 because index is 0-based and columns are 1-based

				if (existingSelectorColumn > 0) {
					// we need to swap
					const temp = sportSelectors[column];

					sportSelectors[column] = {
						name: chosenSelector.name,
						tips: chosenSelector.tips,
						displayTips: chosenSelector.displayTips,
						bettingTypes: chosenSelector.primary,
						secondaryBettingTypes: chosenSelector.secondary,
					};
					sportSelectors[`${existingSelectorColumn}`] = temp;
				} else {
					// do normal update
					sportSelectors[column] = {
						name: chosenSelector.name,
						tips: chosenSelector.tips,
						displayTips: chosenSelector.displayTips,
						bettingTypes: chosenSelector.primary,
						secondaryBettingTypes: chosenSelector.secondary,
					};
				}

				viewStore.configuration.bettingTypeSelectorsStore.setSportSelector(
					sport,
					sportSelectors
				);
			});
		}

		const disabledOptions = Object.keys(sportSelectors)
			.filter(
				(colIdx) =>
					sportSelectors[colIdx] != null &&
					// @ts-expect-error
					colIdx <= options.numberOfColumns
			)
			.map((colIdx) => sportSelectors[colIdx].name);

		return (
			<>
				{[...Array(options.numberOfColumns)].map((_, i) => {
					const colIndex = i + 1;

					if (
						sportSelectors == null ||
						sportSelectors[`${colIndex}`] == null
					) {
						return (
							<div
								key={i}
								className="header--sport__item header--sport__type header--sport__types__item"
							/>
						);
					}

					const columnSelector = sportSelectors[`${colIndex}`];

					return (
						<Dropdown
							key={i}
							disabledOptions={disabledOptions}
							value={columnSelector}
							allAvailable={allAvailableMapped}
							onChange={(newSelectorValue) => {
								// newSelectorValue is selector name and NOT betting type abrv!
								updateSelectors(
									`${colIndex}`,
									newSelectorValue
								);
							}}
						/>
					);
				})}
			</>
		);
	}
);
